export const ROCK5A_CONNECTION_MODES = Object.freeze({
  DIRECT_LAN: 'DIRECT_LAN',
  MANUAL_IP: 'MANUAL_IP',
})

const DEFAULT_CONTROL_PORT = 8770
const DEFAULT_AUDIO_PORT = 8771
const RECENT_DEVICE_KEY = 'rock5aRecentDevice.v1'

function requiredPort(value, fallback) {
  const port = value === undefined || value === null || value === '' ? fallback : Number(value)
  if (!Number.isInteger(port) || port < 1 || port > 65535) {
    const error = new Error(`invalid_port:${value}`)
    error.code = 'INVALID_PORT'
    throw error
  }
  return port
}

export function isValidManualIp(value) {
  const host = String(value || '').trim()
  const parts = host.split('.')
  return parts.length === 4 && parts.every((part) => {
    if (!/^\d{1,3}$/.test(part)) return false
    if (part.length > 1 && part.startsWith('0')) return false
    const octet = Number(part)
    return octet >= 0 && octet <= 255
  })
}

function normaliseHost(device, source) {
  const host = String(device.ip || device.host || '').trim()
  if (!host) {
    const error = new Error('rock5a_host_required')
    error.code = 'HOST_REQUIRED'
    throw error
  }
  if (source === ROCK5A_CONNECTION_MODES.MANUAL_IP && !isValidManualIp(host)) {
    const error = new Error('manual_ip_invalid')
    error.code = 'INVALID_IP'
    throw error
  }
  return host
}

function wsHost(host) {
  return host.includes(':') && !host.startsWith('[') ? `[${host}]` : host
}

export function assertBrowserWebSocketPolicy(context, pageProtocol) {
  const protocol = pageProtocol || (typeof window !== 'undefined' ? window.location.protocol : '')
  const urls = [context.controlWsUrl, context.audioWsUrl, context.assistantWsUrl]
  if (protocol === 'https:' && urls.some((url) => String(url).startsWith('ws://'))) {
    const error = new Error(
      'HTTPS页面不能连接ws://Rock5A。请配置Rock5A的WSS终止点，或用受控HTTP页面进行现场直连。'
    )
    error.code = 'HTTPS_WS_MIXED_CONTENT'
    error.browserBlocked = true
    throw error
  }
  return true
}

export function createRock5aConnectionContext(device, options = {}) {
  const discoverySource = options.discoverySource
    || device.discoverySource
    || ROCK5A_CONNECTION_MODES.DIRECT_LAN
  const host = normaliseHost(device, discoverySource)
  const controlPort = requiredPort(device.controlPort ?? device.port, DEFAULT_CONTROL_PORT)
  const audioPort = requiredPort(device.audioPort ?? device.audio_port, DEFAULT_AUDIO_PORT)
  const secure = device.secure === true || device.ws_protocol === 'wss'
  const protocol = secure ? 'wss' : 'ws'
  const authority = wsHost(host)
  const context = Object.freeze({
    deviceId: String(device.deviceId || device.device_id || device.id || device.hostname || host),
    deviceName: String(device.deviceName || device.name || device.hostname || host),
    host,
    controlPort,
    audioPort,
    controlWsUrl: `${protocol}://${authority}:${controlPort}`,
    audioWsUrl: `${protocol}://${authority}:${audioPort}`,
    assistantWsUrl: `${protocol}://${authority}:${controlPort}`,
    token: String(device.token || options.token || ''),
    discoverySource,
    connectedAt: options.connectedAt || null,
    connectionState: options.connectionState || 'selected',
  })
  assertBrowserWebSocketPolicy(context, options.pageProtocol)
  return context
}

export function connectionContextToDevice(context) {
  if (!context) return null
  return {
    id: context.deviceId,
    name: context.deviceName,
    ip: context.host,
    host: context.host,
    port: context.controlPort,
    audio_port: context.audioPort,
    token: context.token,
    secure: context.controlWsUrl.startsWith('wss://'),
    ws_protocol: context.controlWsUrl.startsWith('wss://') ? 'wss' : 'ws',
  }
}

export function redactConnectionUrl(value) {
  if (!value) return ''
  try {
    const url = new URL(value)
    for (const key of ['token', 'access_token', 'authorization']) {
      if (url.searchParams.has(key)) url.searchParams.set(key, '[REDACTED]')
    }
    return url.toString()
  } catch (_) {
    return String(value).replace(/([?&](?:token|access_token|authorization)=)[^&\s]+/gi, '$1[REDACTED]')
  }
}

function websocketReachable(url, token, options = {}) {
  const timeoutMs = Number(options.timeoutMs || 2500)
  const createWebSocket = options.webSocketFactory || ((target) => new WebSocket(target))
  const target = new URL(url)
  if (token) target.searchParams.set('token', token)
  return new Promise((resolve) => {
    let settled = false
    let socket
    const finish = (result) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      if (socket) {
        socket.onopen = null
        socket.onerror = null
        socket.onclose = null
        try { socket.close(1000, 'reachability_check_complete') } catch (_) {}
      }
      resolve(result)
    }
    const timer = setTimeout(() => finish({ ok: false, reason: 'timeout' }), timeoutMs)
    try {
      socket = createWebSocket(target.toString())
      socket.onopen = () => finish({ ok: true })
      socket.onerror = () => finish({ ok: false, reason: 'websocket_error' })
      socket.onclose = (event) => finish({
        ok: false,
        reason: event && event.reason ? event.reason : 'closed_before_open',
        closeCode: event && event.code,
      })
    } catch (error) {
      finish({ ok: false, reason: error.message || 'socket_create_failed' })
    }
  })
}

export async function probeRock5aConnectionContext(context, options = {}) {
  assertBrowserWebSocketPolicy(context, options.pageProtocol)
  const [control, audio] = await Promise.all([
    websocketReachable(context.controlWsUrl, context.token, options),
    websocketReachable(context.audioWsUrl, context.token, options),
  ])
  return { ok: control.ok && audio.ok, control, audio }
}

export class Rock5aConnectionContextStore {
  constructor(options = {}) {
    this._storage = options.storage || (typeof window !== 'undefined' ? window.localStorage : null)
    this._context = null
    this._listeners = new Set()
  }

  get current() { return this._context }

  subscribe(listener) {
    if (typeof listener !== 'function') return () => {}
    this._listeners.add(listener)
    return () => this._listeners.delete(listener)
  }

  select(device, options = {}) {
    this._context = createRock5aConnectionContext(device, options)
    if (options.saveRecent !== false) this._saveRecent(this._context)
    this._emit()
    return this._context
  }

  updateConnectionState(connectionState, fields = {}) {
    if (!this._context) return null
    this._context = Object.freeze({
      ...this._context,
      ...fields,
      connectionState,
      token: this._context.token,
    })
    this._emit()
    return this._context
  }

  clear() {
    this._context = null
    this._emit()
  }

  loadRecent(options = {}) {
    if (!this._storage) return null
    try {
      const recent = JSON.parse(this._storage.getItem(RECENT_DEVICE_KEY) || 'null')
      if (!recent) return null
      return createRock5aConnectionContext(recent, {
        ...options,
        discoverySource: ROCK5A_CONNECTION_MODES.MANUAL_IP,
      })
    } catch (_) {
      return null
    }
  }

  _saveRecent(context) {
    if (!this._storage) return
    try {
      this._storage.setItem(RECENT_DEVICE_KEY, JSON.stringify({
        deviceId: context.deviceId,
        deviceName: context.deviceName,
        host: context.host,
        controlPort: context.controlPort,
        audioPort: context.audioPort,
        secure: context.controlWsUrl.startsWith('wss://'),
      }))
    } catch (_) {
      // Storage can be unavailable under private browsing policies.
    }
  }

  _emit() {
    for (const listener of this._listeners) listener(this._context)
  }
}

let singleton = null

export function getRock5aConnectionContextStore() {
  if (!singleton) singleton = new Rock5aConnectionContextStore()
  return singleton
}
